import React, { useState } from 'react';
import { THEMES, DEFAULT_THEME_ID, applyTheme, AppTheme } from '../themes';
import './ThemeShowcase.css';

/* ──────────── Mini preview sub-components ──────────── */

function PreviewNav({ isLight }: { isLight?: boolean }) {
  return (
    <nav className={`ts-nav${isLight ? ' ts-nav-light' : ''}`}>
      <span className="ts-nav-logo">⚡ TopQik</span>
      <div className="ts-nav-links">
        {['Matches', 'My Picks', 'Board'].map((l, i) => (
          <span key={l} className={`ts-nav-item${i === 0 ? ' ts-nav-item-active' : ''}`}>{l}</span>
        ))}
      </div>
    </nav>
  );
}

function PreviewHero() {
  return (
    <section className="ts-hero">
      <span className="ts-badge">⚡ AI-Powered Predictions</span>
      <h2 className="ts-headline">
        Predict.{' '}
        <span className="ts-headline-gradient">Compete.<br />Dominate.</span>
      </h2>
      <p className="ts-subline">
        AI-powered predictions to beat the crowd and climb the board.
      </p>
      <div className="ts-ctas">
        <span className="ts-cta-primary">View Matches →</span>
        <span className="ts-cta-secondary">Leaderboard</span>
      </div>
    </section>
  );
}

function PreviewStats() {
  const items = [
    { icon: '🎯', val: '4',   label: 'Fixtures' },
    { icon: '🤖', val: '74%', label: 'AI Acc.' },
    { icon: '🏆', val: '8',   label: 'On Board' },
    { icon: '🔥', val: '—',   label: 'Streak' },
  ];
  return (
    <div className="ts-stats-row">
      {items.map(s => (
        <div key={s.label} className="ts-stat-glass">
          <span className="ts-stat-icon">{s.icon}</span>
          <span className="ts-stat-val">{s.val}</span>
          <span className="ts-stat-label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ──────────── Main page ──────────── */

export default function ThemeShowcase() {
  const [activeId, setActiveId] = useState<string>(
    () => localStorage.getItem('topqik_theme_id') ?? DEFAULT_THEME_ID
  );

  function handleApply(theme: AppTheme) {
    applyTheme(theme);
    setActiveId(theme.id);
  }

  return (
    <div className="ts-page container">
      <div className="matches-header">
        <h1 className="page-title">Theme Showcase</h1>
        <p className="page-sub">
          5 Gen-Z themes — glassmorphism cards, gradient headlines, glowing CTAs.
          Click Apply to switch the entire app instantly.
        </p>
      </div>

      <div className="ts-grid">
        {THEMES.map(theme => {
          const isActive = theme.id === activeId;
          return (
            <div
              key={theme.id}
              className={`ts-card-wrap${isActive ? ' ts-active' : ''}`}
              style={{
                '--ts-a': theme.vars['--ts-a'],
                '--ts-b': theme.vars['--ts-b'],
              } as React.CSSProperties}
            >
              {/* Animated spinning gradient border */}
              <div className="ts-border-spin" />

              {/* Inner card content (sits above the spinning border) */}
              <div className="ts-card-inner">

                {/* Preview window — ALL CSS vars scoped here */}
                <div
                  className={`ts-preview-window ${theme.showcaseClass ?? ''}`}
                  style={theme.vars as React.CSSProperties}
                >
                  {/* Blobs behind glassmorphism cards so blur is visible */}
                  <div className="ts-blob ts-blob-a" />
                  <div className="ts-blob ts-blob-b" />

                  <PreviewNav isLight={theme.isLight} />
                  <div className="ts-preview-body">
                    <PreviewHero />
                    <PreviewStats />
                  </div>
                </div>

                {/* Footer — uses current app theme vars */}
                <div className={`ts-footer${theme.isLight ? ' ts-footer-light' : ''}`}>
                  <div className="ts-meta">
                    <span className="ts-emoji">{theme.emoji}</span>
                    <div>
                      <div className="ts-name">{theme.name}</div>
                      <div className="ts-desc">{theme.description}</div>
                    </div>
                  </div>
                  <button
                    className={`ts-apply${isActive ? ' ts-applied' : ''}`}
                    style={{
                      '--btn-a':    theme.vars['--ts-a'],
                      '--btn-b':    theme.vars['--ts-b'],
                      '--btn-text': theme.vars['--ts-btn-text'],
                    } as React.CSSProperties}
                    onClick={() => handleApply(theme)}
                    disabled={isActive}
                  >
                    {isActive ? '✓ Applied' : 'Apply theme'}
                  </button>
                </div>

              </div>
            </div>
          );
        })}
      </div>

      <p className="ts-note">
        Theme persists to localStorage and loads before first paint — no colour flash on refresh.
      </p>
    </div>
  );
}
